import { useEffect, useState } from 'react';
import { useFormik } from 'formik';

import { initialValues, validationSchema } from './formData';
import { PqrTimesDataProps } from './props';

import { ParamEnum, ISelect } from '../../../../../../shared/types/shared';
import { PqrTime } from '../../../../../../shared/types/paramPqrTimes';
import { asyncGetParams } from '../../../../../../services/param.service';

export const usePqrTimesData = ({
    data,
    onChangeData,
    onChangeFormik,
}: PqrTimesDataProps) => {
    const [receptionChannels, setReceptionChannels] = useState<ISelect[]>([]);
    const [types, setTypes] = useState<ISelect[]>([]);
    const [halfs, setHalfs] = useState<ISelect[]>([]);
    const [subtypeDps, setSubtypeDps] = useState<ISelect[]>([]);
    const [procedures, setProcedures] = useState<ISelect[]>([]);
    const [typeDays, setTypeDays] = useState<ISelect[]>([]);
    const [typeStatus, setTypeStatus] = useState<ISelect[]>([]);

    const onSubmit = (data: PqrTime) => {
        if (onChangeData) onChangeData(data || {});
    };

    const formik = useFormik({
        initialValues,
        validationSchema,
        onSubmit,
    });

    useEffect(() => {
        if (onChangeFormik) onChangeFormik(formik);
    }, [formik.values, formik.errors]);

    useEffect(() => {
        formik?.setFieldValue('status', (data?.status as ISelect)?.id || '');
        formik?.setFieldValue(
            'receptionChannel',
            (data?.receptionChannel as ISelect)?.id || ''
        );
        formik?.setFieldValue('type', (data?.type as ISelect)?.id || '');
        formik?.setFieldValue('half', (data?.half as ISelect)?.id || '');
        formik?.setFieldValue(
            'subtypeDp',
            (data?.subtypeDp as ISelect)?.id || ''
        );
        formik?.setFieldValue(
            'procedure',
            (data?.procedure as ISelect)?.id || ''
        );
        formik?.setFieldValue('name', data?.name || '');
        formik?.setFieldValue('active', data?.active || true);
        formik?.setFieldValue('observation', data?.observation || '');
        formik?.setFieldValue('typeDay', (data?.typeDay as ISelect)?.id || '');
    }, [data]);

    useEffect(() => {
        if (Boolean(formik.values.type)) {
            asyncGetParams(
                ParamEnum.PQR_HALF,
                undefined,
                formik.values.type
            ).then((items) => {
                setHalfs(items);
            });
        }
        if (!data || !Boolean(data.half)) {
            formik?.setFieldValue('half', '');
            formik?.setFieldValue('subtypeDp', '');
        }
    }, [formik.values.type, types]);

    useEffect(() => {
        if (Boolean(formik.values.half)) {
            asyncGetParams(
                ParamEnum.PQR_SUBTYPE_DP,
                undefined,
                formik.values.half
            ).then((items) => setSubtypeDps(items));
        }
        if (!data || !Boolean(data.subtypeDp))
            formik?.setFieldValue('subtypeDp', '');
    }, [formik.values.half]);

    useEffect(() => {
        asyncGetParams(ParamEnum.PQR_RECEPTION_CHANNEL).then((items) =>
            setReceptionChannels(items)
        );
        asyncGetParams(ParamEnum.PQR_TYPE).then((items) => setTypes(items));
        asyncGetParams(ParamEnum.PQR_PROCEDURE).then((items) =>
            setProcedures(items)
        );
        asyncGetParams(ParamEnum.PQR_STATUS).then((items) =>
            setTypeStatus(items)
        );
        asyncGetParams(ParamEnum.BASE_DATE_FOR_DAY_CALCULATION).then((items) =>
            setTypeDays(items)
        );
    }, []);

    const getNameFromId = (
        idOrIds: string | ISelect | (string | ISelect)[],
        options: ISelect[]
    ): string => {
        if (Array.isArray(idOrIds)) {
            return idOrIds
                .map((item) => {
                    if (typeof item === 'string') {
                        return (
                            options.find((opt) => opt.id === item)?.name ?? item
                        );
                    } else {
                        return item.name;
                    }
                })
                .join(', ');
        }
        if (typeof idOrIds === 'string') {
            return options.find((opt) => opt.id === idOrIds)?.name ?? idOrIds;
        }
        return idOrIds.name;
    };

    useEffect(() => {
        const receptionChannelName = getNameFromId(
            formik.values.receptionChannel,
            receptionChannels
        );
        const statusName = getNameFromId(formik.values.status, typeStatus);

        const typePqrName = getNameFromId(formik.values.type, types);

        const halfName = getNameFromId(formik.values.half, halfs);

        const subtypeDpName = getNameFromId(
            formik.values.subtypeDp,
            subtypeDps
        );

        const procedureName = getNameFromId(
            formik.values.procedure,
            procedures
        );
        const nameParts = [
            receptionChannelName,
            statusName,
            typePqrName,
            halfName,
            subtypeDpName,
            procedureName,
        ].filter((part) => part);

        const name = nameParts.join(' | ');

        if (name) formik.setFieldValue('name', name);
    }, [
        formik.values.receptionChannel,
        formik.values.status,
        formik.values.type,
        formik.values.half,
        formik.values.subtypeDp,
        formik.values.procedure,
        halfs,
        subtypeDps,
    ]);

    return {
        formik,
        receptionChannels,
        types,
        halfs,
        subtypeDps,
        procedures,
        typeDays,
        typeStatus,
    };
};
